import { NO_VALUE_DISPLAY, PROVIDER_OPTIONS } from "../../constants";
import { BookingDto, IProduceJoinPassengerPreviewDto } from "../../dto";
import {
  AssignDocksDuringProductionEnum,
  BulkProduceTypeEnum,
  DirectionsServiceEnum,
  EtlBookingTypeEnum,
  ProductionTypeEnum,
} from "../../enums";
import { FormatDateTimeNormalizedFn } from "../../hooks";
import { ITypeormWhereCriteria, RydeUiUnit } from "../../models";
import { IProductionSummaryItem } from "../../models/production/production.models";
import { isISODateStr } from "../date.helpers";
import {
  asOperatorDescriptor,
  extractPassengerTypeIdsFromPassengerCriteria,
  extractSectorsFromProductionCriteria,
  joinDirectionInValues,
} from "./production-criteria-parse.helpers";

const JOIN_DELIMITER = ", ";

export interface IBuildProductionCriteriaSummaryInput {
  criteria?: ITypeormWhereCriteria<BookingDto> &
    IProduceJoinPassengerPreviewDto;
  name?: string;
  producedFrom?: TDateISO;
  producedTo?: TDateISO;
  rideTypeId?: number;
  rideTypeName?: string;
  productionType?: ProductionTypeEnum[];
  productionAction?: BulkProduceTypeEnum;
  directionsService?: DirectionsServiceEnum;
  assignDocksDuringProduction?: AssignDocksDuringProductionEnum;
  sendToSupplierAfterProduce?: boolean;
  doNotLockBookings?: boolean;
  isRejectUnassigned?: boolean;
  dayTypeIds?: number[];
  filterCourseIds?: number[];
  bookingIds?: number[];
}

export interface IProductionCriteriaSummaryDeps {
  t: (key: string) => string;
  translateLabel: (key: string) => string;
  formatDateTimeNormalized: FormatDateTimeNormalizedFn;
  getDropdownLabels: (entity: RydeUiUnit, ids: number[]) => Promise<string[]>;
}

const pickValidSummaryDate = (
  preferred?: TDateISO,
  fallback?: TDateISO,
): TDateISO | undefined => {
  if (typeof preferred === "string" && isISODateStr(preferred)) {
    return preferred;
  }
  if (typeof fallback === "string" && isISODateStr(fallback)) {
    return fallback;
  }
  return undefined;
};

const formatSummaryDateBound = (
  dateIso: TDateISO | undefined,
  formatDateTimeNormalized: FormatDateTimeNormalizedFn,
): string | undefined => {
  if (!dateIso) {
    return undefined;
  }
  const formatted = formatDateTimeNormalized(dateIso);
  if (!formatted || formatted === NO_VALUE_DISPLAY) {
    return undefined;
  }
  return formatted;
};

export const buildProductionCriteriaSummaryItems = async (
  input: IBuildProductionCriteriaSummaryInput,
  deps: IProductionCriteriaSummaryDeps,
): Promise<IProductionSummaryItem[]> => {
  const items: IProductionSummaryItem[] = [];
  const { criteria } = input;
  const passenger = criteria?.passenger;
  const { t, translateLabel, formatDateTimeNormalized, getDropdownLabels } =
    deps;

  if (input.name) {
    items.push({ label: "produceButton.formLabel.name", value: input.name });
  }

  const bookTimeRange = asOperatorDescriptor(criteria?.bookTime)?.$between as
    [TDateISO?, TDateISO?] | undefined;
  const producedFrom = pickValidSummaryDate(
    input.producedFrom,
    bookTimeRange?.[0],
  );
  const producedTo = pickValidSummaryDate(input.producedTo, bookTimeRange?.[1]);
  const fromLabel = formatSummaryDateBound(
    producedFrom,
    formatDateTimeNormalized,
  );
  const toLabel = formatSummaryDateBound(producedTo, formatDateTimeNormalized);

  if (fromLabel && toLabel) {
    items.push({
      label: "booking.formLabel.date",
      value: `${fromLabel} - ${toLabel}`,
    });
  } else if (fromLabel || toLabel) {
    items.push({
      label: "booking.formLabel.date",
      value: fromLabel ?? toLabel ?? "",
    });
  }

  const direction = asOperatorDescriptor(criteria?.direction);
  if (direction?.$eq) {
    items.push({
      label: t("booking.formLabel.direction"),
      value: translateLabel(direction.$eq as string),
    });
  } else {
    const directionIn = direction?.$in as string[] | undefined;
    if (directionIn?.length) {
      items.push({
        label: t("booking.formLabel.direction"),
        value: translateLabel(joinDirectionInValues(directionIn)),
      });
    }
  }

  if (input.productionAction) {
    items.push({
      label: "rideProduction.metricsFilters.action",
      value: t(`booking.generalInfo.${input.productionAction}`),
    });
  }

  if (input.productionType?.length) {
    items.push({
      label: "rideProduction.metricsFilters.productionType",
      value: input.productionType
        .map(type => translateLabel(type))
        .join(JOIN_DELIMITER),
    });
  }

  if (input.rideTypeId || input.rideTypeName) {
    let rideTypeValue = input.rideTypeName;
    if (!rideTypeValue && input.rideTypeId) {
      const rideTypeTitles = await getDropdownLabels("rideType", [
        input.rideTypeId,
      ]);
      rideTypeValue = rideTypeTitles.join(JOIN_DELIMITER);
    }
    if (rideTypeValue) {
      items.push({
        label: "produceButton.formLabel.rideTypeId",
        value: rideTypeValue,
      });
    }
  }

  if (input.directionsService) {
    const engineLabel =
      PROVIDER_OPTIONS.find(option => option.value === input.directionsService)
        ?.label ?? input.directionsService;
    items.push({
      label: "produceButton.formLabel.directionsService",
      value: engineLabel,
    });
  }

  const areaIds = criteria?.stationArea?.$any as number[] | undefined;
  if (areaIds?.length) {
    const areaTitles = await getDropdownLabels("area", areaIds);
    items.push({
      label: "booking.generalInfo.areas",
      value: areaTitles.join(JOIN_DELIMITER),
    });
  }

  const shiftIds = asOperatorDescriptor(criteria?.shiftId)?.$in as
    number[] | undefined;
  if (shiftIds?.length) {
    const shiftTitles = await getDropdownLabels("shift", shiftIds);
    items.push({
      label: "booking.generalInfo.shifts",
      value: shiftTitles.join(JOIN_DELIMITER),
    });
  }

  const unitIds = asOperatorDescriptor(passenger?.unitId)?.$in as
    number[] | undefined;
  if (unitIds?.length) {
    const unitTitles = await getDropdownLabels("unit", unitIds);
    items.push({
      label: "booking.generalInfo.units",
      value: unitTitles.join(JOIN_DELIMITER),
    });
  }

  const siteIds = asOperatorDescriptor(passenger?.siteId)?.$in as
    number[] | undefined;
  if (siteIds?.length) {
    const siteTitles = await getDropdownLabels("site", siteIds);
    items.push({
      label: "booking.generalInfo.sites",
      value: siteTitles.join(JOIN_DELIMITER),
    });
  }

  const passengerTypeIds =
    extractPassengerTypeIdsFromPassengerCriteria(passenger);
  if (passengerTypeIds?.length) {
    const passengerTypeTitles = await getDropdownLabels(
      "passengerType",
      passengerTypeIds,
    );
    items.push({
      label: "booking.generalInfo.passengerTypes",
      value: passengerTypeTitles.join(JOIN_DELIMITER),
    });
  }

  const sectors = extractSectorsFromProductionCriteria(criteria);
  if (sectors?.length) {
    items.push({
      label: "produceButton.formLabel.sectors",
      value: sectors.join(JOIN_DELIMITER),
    });
  }

  const bookingType = criteria?.bookingType?.$eq as
    EtlBookingTypeEnum | undefined;
  if (bookingType) {
    items.push({
      label: "produceButton.formLabel.bookingType",
      value: t(`selectListLabel.${bookingType}`),
    });
  }

  const bookingIds =
    input.bookingIds ??
    (asOperatorDescriptor(criteria?.id)?.$in as number[] | undefined)?.filter(
      Boolean,
    );
  if (bookingIds?.length) {
    items.push({
      label: "rideProduction.metricsFilters.bookingIds",
      value: bookingIds.join(JOIN_DELIMITER),
    });
  }

  if (input.filterCourseIds?.length) {
    const courseTitles = await getDropdownLabels(
      "course",
      input.filterCourseIds,
    );
    items.push({
      label: "rideProduction.metricsFilters.filterCourseIds",
      value: courseTitles.join(JOIN_DELIMITER),
    });
  }

  if (input.dayTypeIds?.length) {
    const dayTypeTitles = await getDropdownLabels("dayType", input.dayTypeIds);
    items.push({
      label: "booking.generalInfo.dayTypes",
      value: dayTypeTitles.join(JOIN_DELIMITER),
    });
  }

  if (input.assignDocksDuringProduction) {
    items.push({
      label: "rideProduction.formLabel.assignDocksDuringProduction",
      value: t(`selectListLabel.${input.assignDocksDuringProduction}`),
    });
  }

  if (input.sendToSupplierAfterProduce) {
    items.push({
      label: "produceButton.formLabel.summary.autoSendToSupplier",
      value: t("general.YES"),
    });
  }

  if (input.doNotLockBookings) {
    items.push({
      label: "rideProduction.formLabel.doNotLockBookings",
      value: t("general.YES"),
    });
  }

  if (input.isRejectUnassigned) {
    items.push({
      label: "rideProduction.formLabel.isRejectUnassigned",
      value: t("general.YES"),
    });
  }

  return items;
};
